// src/pages/SolicitarPropuesta.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header'; // IMPORTAMOS EL HEADER
import SEO from '../components/SEO';

import { 
  ArrowLeftIcon,
  RocketLaunchIcon,
  CheckCircleIcon,
  XMarkIcon,
  DocumentTextIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  UserGroupIcon,
  LightBulbIcon,
  DevicePhoneMobileIcon,
  CloudIcon,
  ServerIcon,
  ShieldCheckIcon,
  PaperAirplaneIcon,
  CodeBracketIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

// Datos de proyectos para referencia (igual que antes)
const proyectos = [
  {
    id: 1,
    title: "Sistema de Inspecciones",
    category: "PWA Avanzado",
    description: "Aplicativo web PWA para inspecciones de mantenimiento, seguridad industrial y medio ambiente."
  },
  {
    id: 2,
    title: "Gestión de Riesgos",
    category: "Sistema de Gestión",
    description: "Identificación de peligros y evaluación de riesgos en puestos de trabajo."
  },
  {
    id: 3,
    title: "Órdenes de Trabajo",
    category: "Productividad",
    description: "Gestión de tareas con tablero Kanban y notificaciones automáticas."
  },
  {
    id: 4,
    title: "Registro de Visitas",
    category: "Control de Acceso",
    description: "Control de visitas con geolocalización, fotos y dictado por voz."
  },
  {
    id: 5,
    title: "Investigación de Accidentes",
    category: "Seguridad Industrial",
    description: "Análisis con método de espina de pescado y 5 porqués."
  },
  {
    id: 6,
    title: "Gestión de RRHH",
    category: "Recursos Humanos",
    description: "Administración de empleados y capacitaciones."
  },
  {
    id: 7,
    title: "Firma Digital",
    category: "Documentación",
    description: "Sistema de firma digital con trazabilidad completa."
  },
  {
    id: 8,
    title: "Estudios Ergonómicos",
    category: "Salud Ocupacional",
    description: "Protocolos según normativa argentina."
  },
  {
    id: 9,
    title: "Mediciones Ambientales",
    category: "Medio Ambiente",
    description: "Registro de ruido e iluminación según resolución legal."
  },
  {
    id: 10,
    title: "Avisos de Riesgos",
    category: "Gestión de Riesgos",
    description: "Gestión de alertas por criticidad y responsables."
  },
  {
    id: 11,
    title: "Control de Pesaje",
    category: "Logística Inteligente",
    description: "Registro de kilos en cargas/descargas con PDF detallado."
  }
];

const SolicitarPropuesta: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [selectedProject, setSelectedProject] = useState<typeof proyectos[0] | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Obtener datos del proyecto desde la navegación
  const projectId = location.state?.projectId;

  // Buscar proyecto por ID inicialmente
  useEffect(() => {
    if (projectId) {
      const proyectoEncontrado = proyectos.find(p => p.id === projectId);
      if (proyectoEncontrado) {
        setSelectedProject(proyectoEncontrado);
      }
    }
  }, [projectId]);

  // Form state
  const [formData, setFormData] = useState({
    // Información del proyecto
    projectId: projectId ? projectId.toString() : '',
    projectTitle: '',
    projectCategory: '',
    
    // Información del cliente
    nombre: '',
    email: '',
    telefono: '',
    empresa: '',
    cargo: '',
    industria: '',
    
    // Requerimientos del proyecto
    descripcionProyecto: '',
    objetivos: '',
    plazoEstimado: '3 meses',
    presupuestoAproximado: '5000-10000',
    usuariosFinales: '10-50',
    
    // Características deseadas
    caracteristicas: [] as string[],
    integraciones: [] as string[],
    prioridades: [] as string[],
    
    // Información adicional
    comentariosAdicionales: '',
    preferenciaContacto: 'email',
    disponibilidadReunion: 'mañana'
  });

  // Opciones para características
  const opcionesCaracteristicas = [
    "Dashboard analítico",
    "Modo offline",
    "Notificaciones push",
    "Reportes PDF/Excel",
    "Integración móvil",
    "Multi-idioma",
    "Firma digital",
    "Geolocalización",
    "API para integración",
    "Soporte 24/7"
  ];

  // Opciones para integraciones
  const opcionesIntegraciones = [
    "Sistema ERP existente",
    "Bases de datos legacy",
    "APIs de terceros",
    "Sistemas de pago",
    "Redes sociales",
    "Servicios cloud (AWS, Azure)",
    "Sistemas de mensajería",
    "Hardware específico"
  ];

  // Opciones para prioridades
  const opcionesPrioridades = [
    "Cumplimiento de plazo",
    "Presupuesto limitado",
    "Calidad y estabilidad",
    "Escalabilidad futura",
    "Facilidad de uso",
    "Soporte continuo"
  ];

  // Actualizar formData cuando selectedProject cambia
  useEffect(() => {
    if (selectedProject) {
      setFormData(prev => ({
        ...prev,
        projectId: selectedProject.id.toString(),
        projectTitle: selectedProject.title,
        projectCategory: selectedProject.category
      }));
    }
  }, [selectedProject]);

  // Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (category: 'caracteristicas' | 'integraciones' | 'prioridades', value: string) => {
    setFormData(prev => {
      const currentValues = [...prev[category]];
      if (currentValues.includes(value)) {
        return {
          ...prev,
          [category]: currentValues.filter(item => item !== value)
        };
      } else {
        return {
          ...prev,
          [category]: [...currentValues, value]
        };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // URL base dinámica
      const getApiBaseUrl = () => {
        if (typeof window !== 'undefined') {
          const hostname = window.location.hostname;
          const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
          
          if (isLocalhost) {
            return 'http://localhost:3000/api';
          } else if (hostname.includes('vercel.app')) {
            return `https://${hostname}/api`;
          } else {
            return '/api';
          }
        }
        return '/api';
      };

      const apiUrl = `${getApiBaseUrl()}/send-proposal-request`;
      
      console.log('🚀 Enviando solicitud de propuesta a:', apiUrl);
      
      // Configurar timeout más largo para propuestas complejas
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 45000);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Respuesta recibida:', data);

      if (!data.success) {
        throw new Error(data.message || 'Error en la respuesta del servidor');
      }

      // Éxito
      setRequestId(data.requestId || null);
      setIsSubmitted(true);
      
      // Reiniciar formulario después de 3 segundos
      setTimeout(() => {
        setFormData({
          projectId: '',
          projectTitle: '',
          projectCategory: '',
          nombre: '',
          email: '',
          telefono: '',
          empresa: '',
          cargo: '',
          industria: '',
          descripcionProyecto: '',
          objetivos: '',
          plazoEstimado: '3 meses',
          presupuestoAproximado: '5000-10000',
          usuariosFinales: '10-50',
          caracteristicas: [],
          integraciones: [],
          prioridades: [],
          comentariosAdicionales: '',
          preferenciaContacto: 'email',
          disponibilidadReunion: 'mañana'
        });
        setSelectedProject(null);
      }, 3000);

    } catch (err) {
      console.error('❌ Error al enviar el formulario:', err);
      
      let errorMessage = 'Error al enviar la solicitud';
      
      if (err instanceof Error) {
        if (err.name === 'AbortError') {
          errorMessage = 'El servidor tardó demasiado en responder. Por favor, intenta nuevamente.';
        } else if (err.message.includes('Failed to fetch')) {
          errorMessage = 'No se pudo conectar al servidor. Verifica tu conexión a internet.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewProjectRequest = () => {
    setSelectedProject(null);
    setShowProjectSelector(false);
    
    setFormData(prev => ({
      ...prev,
      projectId: '',
      projectTitle: '',
      projectCategory: ''
    }));
  };

  const handleSelectProject = (proyecto: typeof proyectos[0]) => {
    setSelectedProject(proyecto);
    setShowProjectSelector(false);
    
    window.history.replaceState({
      projectId: proyecto.id,
      projectTitle: proyecto.title,
      projectCategory: proyecto.category
    }, '');
  };

  const handleCustomProjectSubmit = () => {
    setSelectedProject(null);
    setShowProjectSelector(false);
  };

  // Animaciones
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut" as const
      }
    }
  };

  if (isSubmitted) {
    return (
      <>
  <SEO
    title="Solicitar Propuesta | Algorithmia - Desarrollo de Software"
    description="Solicitá una propuesta personalizada para tu proyecto de software. Contanos tus necesidades y te enviamos un presupuesto detallado con timeline y plan de implementación."
    keywords="solicitar propuesta software, presupuesto desarrollo software, cotización sistema gestión"
    url="https://algorithmia.com.ar/solicitar-propuesta"
    canonicalUrl="https://algorithmia.com.ar/solicitar-propuesta"
    noIndex={true}  // ✅ formulario interno, mejor sin indexar
  />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        {/* Header reutilizable */}
        <Header isSplashLoading={false} />

        {/* Contenido de éxito - Ajustado padding-top */}
        <div className="pt-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-8 sm:p-12 shadow-2xl border-3 border-transparent bg-gradient-to-b from-white to-white bg-origin-border hover:border-gradient-to-r hover:from-green-500 hover:via-teal-600 hover:to-blue-600"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-green-500 via-teal-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
              <CheckCircleIcon className="h-10 w-10 text-white" />
            </div>
            
            <h1 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-6">
              ¡Solicitud de Propuesta Enviada!
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 text-center max-w-3xl mx-auto mb-10">
              Hemos recibido tu solicitud para <span className="font-semibold text-blue-600">{formData.projectTitle || 'un nuevo proyecto'}</span>. 
              Nuestro equipo se pondrá en contacto contigo en las próximas 24-48 horas para discutir los detalles.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                <DocumentTextIcon className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-3">Proceso Siguiente</h3>
                <p className="text-gray-600 text-sm">
                  1. Análisis de requerimientos<br />
                  2. Propuesta detallada<br />
                  3. Revisión conjunta<br />
                  4. Plan de implementación
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                <CalendarIcon className="h-8 w-8 text-green-600 mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-3">Próximos Pasos</h3>
                <p className="text-gray-600 text-sm">
                  • Contactaremos para agendar reunión<br />
                  • Presentaremos propuesta inicial<br />
                  • Definiremos timeline y presupuesto<br />
                  • Iniciaremos plan de trabajo
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                <UserGroupIcon className="h-8 w-8 text-purple-600 mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-3">Equipo Asignado</h3>
                <p className="text-gray-600 text-sm">
                  Un equipo especializado evaluará tu proyecto y te asignará:<br />
                  • Project Manager<br />
                  • Especialista técnico<br />
                  • Consultor de negocio
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/proyectos')}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-green-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                Ver Más Proyectos
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/')}
                className="px-8 py-3 bg-white text-gray-700 font-semibold rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all duration-300 flex items-center justify-center gap-3"
              >
                <RocketLaunchIcon className="h-5 w-5" />
                Volver al Inicio
              </motion.button>
            </div>
            
            <p className="text-gray-500 text-center text-sm">
              ¿Tienes preguntas urgentes? <a href="mailto:contacto@algorithmia.com" className="text-blue-600 font-semibold hover:text-blue-700 hover:underline">contacto@algorithmia.com</a>
            </p>
          </motion.div>
        </div>
      </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header reutilizable */}
      <Header isSplashLoading={false} />

      {/* Hero Section - Ajustado padding-top */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-blue-900 via-blue-700 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Solicita tu <span className="bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent">Propuesta Personalizada</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 max-w-3xl mx-auto">
              Cuéntanos sobre tu proyecto y te enviaremos una propuesta detallada con timeline, presupuesto y plan de implementación.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Indicador de progreso */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-green-500 rounded-full text-white font-bold flex items-center justify-center">
                1
              </div>
              <span className="text-gray-900 font-medium">Información del Proyecto</span>
            </div>
            
            <div className="flex items-center gap-3 opacity-50">
              <div className="w-8 h-8 bg-gray-200 rounded-full text-gray-500 font-bold flex items-center justify-center">
                2
              </div>
              <span className="text-gray-500">Datos del Cliente</span>
            </div>
            
            <div className="flex items-center gap-3 opacity-50">
              <div className="w-8 h-8 bg-gray-200 rounded-full text-gray-500 font-bold flex items-center justify-center">
                3
              </div>
              <span className="text-gray-500">Requerimientos</span>
            </div>
            
            <div className="flex items-center gap-3 opacity-50">
              <div className="w-8 h-8 bg-gray-200 rounded-full text-gray-500 font-bold flex items-center justify-center">
                4
              </div>
              <span className="text-gray-500">Envío</span>
            </div>
          </div>
        </div>
      </div>

      {/* Formulario Principal */}
      <motion.form
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        onSubmit={handleSubmit}
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        {/* Sección 1: Información del Proyecto */}
        <motion.section variants={itemVariants} className="mb-8">
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">Información del Proyecto</h2>
              </div>
              <p className="text-gray-600">
                Selecciona el proyecto de referencia o describe uno nuevo
              </p>
            </div>

            <div className="space-y-6">
              {selectedProject && !showProjectSelector ? (
                <div className="p-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-200">
                  <div className="inline-block px-3 py-1 bg-gradient-to-r from-blue-600 to-green-500 text-white text-xs font-semibold rounded-full mb-4">
                    Proyecto Seleccionado
                  </div>
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedProject.title}</h3>
                    <p className="text-sm text-gray-500 font-medium mb-3">{selectedProject.category}</p>
                    <p className="text-gray-600">{selectedProject.description}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleNewProjectRequest}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-2"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    Cambiar Proyecto
                  </button>
                </div>
              ) : (
                <>
                  {showProjectSelector ? (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Selecciona un proyecto de referencia</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 max-h-80 overflow-y-auto p-2">
                        {proyectos.map((proyecto) => (
                          <div
                            key={proyecto.id}
                            onClick={() => handleSelectProject(proyecto)}
                            className="p-4 border border-gray-200 rounded-xl hover:border-blue-500 hover:shadow-md transition-all duration-300 cursor-pointer bg-white hover:bg-blue-50"
                          >
                            <h4 className="font-bold text-gray-900 mb-1">{proyecto.title}</h4>
                            <p className="text-xs text-blue-600 font-medium mb-2">{proyecto.category}</p>
                            <p className="text-sm text-gray-600">{proyecto.description}</p>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowProjectSelector(false)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                      >
                        ← Prefiero describir mi propio proyecto
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Describir proyecto personalizado</h3>
                        <button
                          type="button"
                          onClick={() => setShowProjectSelector(true)}
                          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-md hover:shadow-lg text-sm flex items-center gap-2"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                          Ver proyectos de referencia
                        </button>
                      </div>
                      
                      <div>
                        <label htmlFor="projectTitle" className="block text-sm font-medium text-gray-700 mb-2">
                          Nombre del Proyecto *
                        </label>
                        <input
                          type="text"
                          id="projectTitle"
                          name="projectTitle"
                          value={formData.projectTitle}
                          onChange={(e) => {
                            handleInputChange(e);
                            handleCustomProjectSubmit();
                          }}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          placeholder="Ej: Sistema de Gestión de Inventarios"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="projectCategory" className="block text-sm font-medium text-gray-700 mb-2">
                          Categoría *
                        </label>
                        <select
                          id="projectCategory"
                          name="projectCategory"
                          value={formData.projectCategory}
                          onChange={(e) => {
                            handleInputChange(e);
                            handleCustomProjectSubmit();
                          }}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        >
                          <option value="">Seleccionar categoría</option>
                          <option value="PWA Avanzado">PWA Avanzado</option>
                          <option value="Sistema de Gestión">Sistema de Gestión</option>
                          <option value="Productividad">Productividad</option>
                          <option value="Control de Acceso">Control de Acceso</option>
                          <option value="Seguridad Industrial">Seguridad Industrial</option>
                          <option value="Recursos Humanos">Recursos Humanos</option>
                          <option value="Documentación">Documentación</option>
                          <option value="Salud Ocupacional">Salud Ocupacional</option>
                          <option value="Medio Ambiente">Medio Ambiente</option>
                          <option value="Logística Inteligente">Logística Inteligente</option>
                          <option value="Personalizado">Personalizado</option>
                        </select>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </motion.section>

        {/* Sección 2: Información del Cliente */}
        <motion.section variants={itemVariants} className="mb-8">
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <UserGroupIcon className="h-6 w-6 text-green-600" />
                <h2 className="text-2xl font-bold text-gray-900">Información del Cliente</h2>
              </div>
              <p className="text-gray-600">
                Datos de contacto para procesar tu solicitud
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="Tu nombre y apellido"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="ejemplo@empresa.com"
                />
              </div>
              
              <div>
                <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="+54 (351) 123-4567"
                />
              </div>
              
              <div>
                <label htmlFor="empresa" className="block text-sm font-medium text-gray-700 mb-2">
                  Empresa *
                </label>
                <input
                  type="text"
                  id="empresa"
                  name="empresa"
                  value={formData.empresa}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="Nombre de tu empresa"
                />
              </div>
              
              <div>
                <label htmlFor="cargo" className="block text-sm font-medium text-gray-700 mb-2">
                  Cargo / Posición
                </label>
                <input
                  type="text"
                  id="cargo"
                  name="cargo"
                  value={formData.cargo}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="CEO, Gerente, Supervisor, etc."
                />
              </div>
              
              <div>
                <label htmlFor="industria" className="block text-sm font-medium text-gray-700 mb-2">
                  Industria / Sector *
                </label>
                <select
                  id="industria"
                  name="industria"
                  value={formData.industria}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="">Seleccionar industria</option>
                  <option value="Manufactura">Manufactura</option>
                  <option value="Logística">Logística y Transporte</option>
                  <option value="Construcción">Construcción</option>
                  <option value="Salud">Salud y Farmacéutica</option>
                  <option value="Retail">Retail y Comercio</option>
                  <option value="Servicios">Servicios Profesionales</option>
                  <option value="Tecnología">Tecnología</option>
                  <option value="Educación">Educación</option>
                  <option value="Gobierno">Gobierno</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Sección 3: Descripción del Proyecto */}
        <motion.section variants={itemVariants} className="mb-8">
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <LightBulbIcon className="h-6 w-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900">Descripción del Proyecto</h2>
              </div>
              <p className="text-gray-600">
                Cuéntanos en detalle lo que necesitas desarrollar
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="descripcionProyecto" className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción Detallada *
                </label>
                <textarea
                  id="descripcionProyecto"
                  name="descripcionProyecto"
                  value={formData.descripcionProyecto}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[120px]"
                  placeholder="Describe tu proyecto en detalle: problema a resolver, funcionalidades principales, usuarios objetivo, etc."
                />
              </div>
              
              <div>
                <label htmlFor="objetivos" className="block text-sm font-medium text-gray-700 mb-2">
                  Objetivos Principales *
                </label>
                <textarea
                  id="objetivos"
                  name="objetivos"
                  value={formData.objetivos}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[100px]"
                  placeholder="¿Qué resultados esperas obtener con este proyecto?"
                />
              </div>
            </div>
          </div>
        </motion.section>

        {/* Sección 4: Características Deseadas */}
        <motion.section variants={itemVariants} className="mb-8">
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <DevicePhoneMobileIcon className="h-6 w-6 text-purple-600" />
                <h2 className="text-2xl font-bold text-gray-900">Características Deseadas</h2>
              </div>
              <p className="text-gray-600">
                Selecciona las funcionalidades que necesitas
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Funcionalidades Principales</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {opcionesCaracteristicas.map((caracteristica, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                      <input
                        type="checkbox"
                        id={`caracteristica-${index}`}
                        checked={formData.caracteristicas.includes(caracteristica)}
                        onChange={() => handleCheckboxChange('caracteristicas', caracteristica)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`caracteristica-${index}`} className="text-gray-700 cursor-pointer select-none">
                        {caracteristica}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Integraciones Necesarias</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {opcionesIntegraciones.map((integracion, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                      <input
                        type="checkbox"
                        id={`integracion-${index}`}
                        checked={formData.integraciones.includes(integracion)}
                        onChange={() => handleCheckboxChange('integraciones', integracion)}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`integracion-${index}`} className="text-gray-700 cursor-pointer select-none">
                        {integracion}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Sección 5: Parámetros del Proyecto */}
        <motion.section variants={itemVariants} className="mb-8">
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <CalendarIcon className="h-6 w-6 text-teal-600" />
                <h2 className="text-2xl font-bold text-gray-900">Parámetros del Proyecto</h2>
              </div>
              <p className="text-gray-600">
                Define los límites y expectativas del desarrollo
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
              <div>
                <label htmlFor="plazoEstimado" className="block text-sm font-medium text-gray-700 mb-2">
                  Plazo Estimado *
                </label>
                <select
                  id="plazoEstimado"
                  name="plazoEstimado"
                  value={formData.plazoEstimado}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="1 mes">1 mes</option>
                  <option value="1-2 meses">1-2 meses</option>
                  <option value="3 meses">3 meses</option>
                  <option value="4-6 meses">4-6 meses</option>
                  <option value="6+ meses">6+ meses</option>
                  <option value="Por definir">Por definir</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="presupuestoAproximado" className="block text-sm font-medium text-gray-700 mb-2">
                  Rango de Presupuesto *
                </label>
                <select
                  id="presupuestoAproximado"
                  name="presupuestoAproximado"
                  value={formData.presupuestoAproximado}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="1000-5000">USD 1,000 - 5,000</option>
                  <option value="5000-10000">USD 5,000 - 10,000</option>
                  <option value="10000-25000">USD 10,000 - 25,000</option>
                  <option value="25000-50000">USD 25,000 - 50,000</option>
                  <option value="50000+">USD 50,000+</option>
                  <option value="Por definir">Por definir</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="usuariosFinales" className="block text-sm font-medium text-gray-700 mb-2">
                  Usuarios Finales *
                </label>
                <select
                  id="usuariosFinales"
                  name="usuariosFinales"
                  value={formData.usuariosFinales}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="1-10">1-10 usuarios</option>
                  <option value="10-50">10-50 usuarios</option>
                  <option value="50-200">50-200 usuarios</option>
                  <option value="200-1000">200-1000 usuarios</option>
                  <option value="1000+">1000+ usuarios</option>
                </select>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Prioridades del Proyecto</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {opcionesPrioridades.map((prioridad, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                    <input
                      type="checkbox"
                      id={`prioridad-${index}`}
                      checked={formData.prioridades.includes(prioridad)}
                      onChange={() => handleCheckboxChange('prioridades', prioridad)}
                      className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`prioridad-${index}`} className="text-gray-700 cursor-pointer select-none">
                      {prioridad}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        {/* Sección 6: Información Adicional */}
        <motion.section variants={itemVariants} className="mb-8">
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <CloudIcon className="h-6 w-6 text-indigo-600" />
                <h2 className="text-2xl font-bold text-gray-900">Información Adicional</h2>
              </div>
              <p className="text-gray-600">
                Preferencias de contacto y comentarios finales
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="preferenciaContacto" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferencia de Contacto *
                </label>
                <select
                  id="preferenciaContacto"
                  name="preferenciaContacto"
                  value={formData.preferenciaContacto}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="email">Email</option>
                  <option value="telefono">Llamada telefónica</option>
                  <option value="videollamada">Videollamada</option>
                  <option value="presencial">Reunión presencial</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="disponibilidadReunion" className="block text-sm font-medium text-gray-700 mb-2">
                  Disponibilidad para Reunión *
                </label>
                <select
                  id="disponibilidadReunion"
                  name="disponibilidadReunion"
                  value={formData.disponibilidadReunion}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="mañana">Mañana (9:00 - 12:00)</option>
                  <option value="tarde">Tarde (14:00 - 18:00)</option>
                  <option value="flexible">Horario flexible</option>
                  <option value="fines">Fines de semana</option>
                </select>
              </div>
            </div>
            
            <div>
              <label htmlFor="comentariosAdicionales" className="block text-sm font-medium text-gray-700 mb-2">
                Comentarios o Requerimientos Especiales
              </label>
              <textarea
                id="comentariosAdicionales"
                name="comentariosAdicionales"
                value={formData.comentariosAdicionales}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[100px]"
                placeholder="Información adicional que consideres relevante para tu proyecto..."
              />
            </div>
          </div>
        </motion.section>

        {/* Beneficios del Proceso */}
        <motion.section variants={itemVariants} className="mb-8">
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-8 border border-gray-200">
            <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
              ¿Qué incluye nuestra propuesta?
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-sm text-center border border-gray-100">
                <DocumentTextIcon className="h-8 w-8 text-blue-600 mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Propuesta Detallada</h4>
                <p className="text-sm text-gray-600">
                  Documento completo con alcance, timeline y costos desglosados
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm text-center border border-gray-100">
                <CalendarIcon className="h-8 w-8 text-green-600 mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Plan de Implementación</h4>
                <p className="text-sm text-gray-600">
                  Cronograma detallado con hitos y entregables
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm text-center border border-gray-100">
                <CurrencyDollarIcon className="h-8 w-8 text-amber-600 mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Presupuesto Transparente</h4>
                <p className="text-sm text-gray-600">
                  Costos desglosados por fases y funcionalidades
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm text-center border border-gray-100">
                <ShieldCheckIcon className="h-8 w-8 text-emerald-600 mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Garantía de Calidad</h4>
                <p className="text-sm text-gray-600">
                  Aseguramos los más altos estándares de desarrollo
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Botones de Envío */}
        <motion.div variants={itemVariants} className="mb-8 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            type="button"
            onClick={() => navigate('/proyectos')}
            className="px-8 py-3 bg-gray-100 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center justify-center gap-3"
          >
            <XMarkIcon className="h-5 w-5" />
            Cancelar
          </button>
          
          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-green-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-70"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Enviando Solicitud...
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="h-5 w-5" />
                Enviar Solicitud de Propuesta
              </>
            )}
          </motion.button>
        </motion.div>

        {/* Nota de confidencialidad */}
        <motion.div variants={itemVariants} className="p-6 bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl border border-green-200">
          <div className="flex items-start gap-4">
            <ShieldCheckIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800">
              <span className="font-semibold">Tu información es confidencial</span> y solo será utilizada para preparar tu propuesta personalizada. 
              No compartimos datos con terceros. Cumplimos con las normativas de protección de datos vigentes.
            </p>
          </div>
        </motion.div>
      </motion.form>
    </div>
  );
};

export default SolicitarPropuesta;